import type { WorkspaceServerService } from '@affine/core/modules/cloud/services/workspace-server';
import type { GlobalState } from '@affine/core/modules/storage';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import {
  isWorkspacePermissionCacheFresh,
  resolveWorkspaceLocalAuthorityAccountId,
  WORKSPACE_PERMISSION_CACHE_VERSION,
  workspacePermissionCacheOwnerKey,
} from '@affine/core/modules/workspace/utils/permission-cache-identity';
import type {
  WorkspaceMetadataChange,
  WorkspacePermissionChange,
} from '@affine/core/modules/workspace-projection-reconciliation/types';
import {
  isBackendAuthOrPermissionError,
  isBackendKnownUnavailable,
  onBackendAvailable,
} from '@affine/core/utils/backend-availability';
import { LiveData, Store } from '@toeverything/infra';
import { map, type Observable } from 'rxjs';

import type { FolderPermissions, FolderRole } from '../services/folder-api';
import type {
  WorkspaceAreaApiService,
  WorkspaceAreaRef,
} from '../services/workspace-area-api';

const WORKSPACE_AREA_CACHE_PREFIX = 'streamlain:workspace-area-sections';
const WORKSPACE_AREA_KINDS = new Set(['public', 'teamspace', 'private', 'app']);
const WORKSPACE_AREA_ROLES = new Set([
  'Owner',
  'Admin',
  'Editor',
  'Reader',
  'None',
]);
const LOCAL_MUTATION_ECHO_TTL_MS = 30_000;

function createLocalMutationId(
  operation: 'create' | 'trash' | 'update'
): string {
  const randomId =
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `workspace-area:${operation}:${randomId}`;
}

interface CachedWorkspaceAreaSections {
  areas: WorkspaceAreaRef[];
  cacheOwnerKey: string;
  manageableAreas?: WorkspaceAreaRef[];
  updatedAt: number;
  version: typeof WORKSPACE_PERMISSION_CACHE_VERSION;
  workspaceId: string;
}

interface WorkspaceAreaCacheSnapshot {
  areas: WorkspaceAreaRef[];
  manageableAreas: WorkspaceAreaRef[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

function workspaceAreaCacheKey(workspaceId: string, cacheOwnerKey: string) {
  return `${WORKSPACE_AREA_CACHE_PREFIX}:${workspaceId}:${cacheOwnerKey}`;
}

function normalizeFolderPermissions(input: unknown): FolderPermissions {
  const raw = isRecord(input) ? input : {};
  return {
    Folder_AddDoc: raw.Folder_AddDoc === true,
    Folder_CreateSubfolder: raw.Folder_CreateSubfolder === true,
    Folder_Delete: raw.Folder_Delete === true,
    Folder_Move: raw.Folder_Move === true,
    Folder_Read: raw.Folder_Read === true,
    Folder_RemoveDoc: raw.Folder_RemoveDoc === true,
    Folder_Restore: raw.Folder_Restore === true,
    Folder_Trash: raw.Folder_Trash === true,
    Folder_Update: raw.Folder_Update === true,
    Folder_Users_Manage: raw.Folder_Users_Manage === true,
    Folder_Users_Read: raw.Folder_Users_Read === true,
  };
}

function normalizeWorkspaceAreaRef(
  input: unknown,
  workspaceId: string
): WorkspaceAreaRef | null {
  if (!isRecord(input)) {
    return null;
  }

  const kind = input.kind;
  const name = input.name;
  const rootFolderId = input.rootFolderId;
  const visibility = input.visibility;
  if (
    typeof kind !== 'string' ||
    !WORKSPACE_AREA_KINDS.has(kind) ||
    typeof name !== 'string' ||
    typeof rootFolderId !== 'string' ||
    (visibility !== 'public' && visibility !== 'private') ||
    input.workspaceId !== workspaceId
  ) {
    return null;
  }

  const effectiveRole =
    typeof input.effectiveRole === 'string' &&
    WORKSPACE_AREA_ROLES.has(input.effectiveRole)
      ? (input.effectiveRole as FolderRole)
      : ('None' as FolderRole);
  const areaKind = kind as WorkspaceAreaRef['kind'];

  return {
    effectiveRole,
    immutable: input.immutable === true,
    kind: areaKind,
    name,
    ownerUserId:
      typeof input.ownerUserId === 'string' ? input.ownerUserId : null,
    permissions: normalizeFolderPermissions(input.permissions),
    rootFolderId,
    systemKind: typeof input.systemKind === 'string' ? input.systemKind : null,
    systemManaged: input.systemManaged === true,
    visibility,
    workspaceId,
  };
}

function areaRefsEqual(
  left: WorkspaceAreaRef,
  right: WorkspaceAreaRef
): boolean {
  return (
    left.effectiveRole === right.effectiveRole &&
    left.immutable === right.immutable &&
    left.kind === right.kind &&
    left.name === right.name &&
    left.ownerUserId === right.ownerUserId &&
    left.rootFolderId === right.rootFolderId &&
    left.systemKind === right.systemKind &&
    left.systemManaged === right.systemManaged &&
    left.visibility === right.visibility &&
    left.workspaceId === right.workspaceId &&
    Object.keys(left.permissions).every(
      key =>
        left.permissions[key as keyof typeof left.permissions] ===
        right.permissions[key as keyof typeof right.permissions]
    )
  );
}

function mergeNewerLocalAreaState(
  serverAreas: WorkspaceAreaRef[],
  beforeRequest: WorkspaceAreaRef[],
  current: WorkspaceAreaRef[]
): WorkspaceAreaRef[] {
  const beforeById = new Map(
    beforeRequest.map(area => [area.rootFolderId, area])
  );
  const currentById = new Map(current.map(area => [area.rootFolderId, area]));
  const removedIds = new Set(
    beforeRequest
      .filter(area => !currentById.has(area.rootFolderId))
      .map(area => area.rootFolderId)
  );
  const newerLocal = current.filter(area => {
    const previous = beforeById.get(area.rootFolderId);
    return !previous || !areaRefsEqual(previous, area);
  });
  const newerLocalIds = new Set(newerLocal.map(area => area.rootFolderId));
  return [
    ...serverAreas.filter(
      area =>
        !removedIds.has(area.rootFolderId) &&
        !newerLocalIds.has(area.rootFolderId)
    ),
    ...newerLocal,
  ].sort((left, right) => {
    const byKind = areaSortRank(left.kind) - areaSortRank(right.kind);
    return byKind === 0 ? left.name.localeCompare(right.name) : byKind;
  });
}

export class WorkspaceAreaCacheStore extends Store {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceAreaApi: WorkspaceAreaApiService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly globalState: GlobalState
  ) {
    super();
  }

  private readonly areas$ = new LiveData<WorkspaceAreaRef[]>([]);
  private readonly manageableAreas$ = new LiveData<WorkspaceAreaRef[]>([]);
  readonly isPopulated$ = new LiveData<boolean>(false);
  readonly isLocalCacheLoaded$ = new LiveData<boolean>(false);
  readonly isServerConfirmed$ = new LiveData<boolean>(false);
  readonly isStale$ = new LiveData<boolean>(false);

  private fetchTriggered = false;
  private localCacheLoaded = false;
  private serverFetchCompleted = false;
  private authoritativeAccessDenied = false;
  private refreshPromise: Promise<void> | null = null;
  private refreshAgain = false;
  private areaStateRevision = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private onlineHandler: (() => void) | null = null;
  private backendAvailabilityUnsubscribe: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly localMutationEchoes = new Map<
    string,
    ReturnType<typeof setTimeout> | null
  >();

  private get workspaceId(): string | null {
    return this.workspaceService.workspace?.id ?? null;
  }

  private get cacheOwnerKey(): string | null {
    const server = this.workspaceServerService.server;
    const workspaceId = this.workspaceId;
    if (!server || !workspaceId) {
      return null;
    }
    const accountId = resolveWorkspaceLocalAuthorityAccountId({
      liveAccountId: server.confirmedAccountId,
      cachedAccountId: server.cachedAccountId,
      scopeReader: this.globalState,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
      workspaceId,
    });
    if (!accountId) return null;
    return workspacePermissionCacheOwnerKey({
      accountId,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
    });
  }

  private get cacheKey(): string | null {
    const workspaceId = this.workspaceId;
    const cacheOwnerKey = this.cacheOwnerKey;
    if (!workspaceId || !cacheOwnerKey) {
      return null;
    }
    return workspaceAreaCacheKey(workspaceId, cacheOwnerKey);
  }

  private readLocalCache(): WorkspaceAreaCacheSnapshot | null {
    const workspaceId = this.workspaceId;
    const cacheOwnerKey = this.cacheOwnerKey;
    const cacheKey = this.cacheKey;
    if (!workspaceId || !cacheOwnerKey || !cacheKey) {
      return null;
    }

    try {
      const cached =
        this.globalState.get<Partial<CachedWorkspaceAreaSections>>(cacheKey);
      if (
        !cached ||
        cached.version !== WORKSPACE_PERMISSION_CACHE_VERSION ||
        cached.workspaceId !== workspaceId ||
        cached.cacheOwnerKey !== cacheOwnerKey ||
        !isWorkspacePermissionCacheFresh(cached.updatedAt) ||
        !Array.isArray(cached.areas)
      ) {
        return null;
      }
      const areas = cached.areas
        .map(area => normalizeWorkspaceAreaRef(area, workspaceId))
        .filter((area): area is WorkspaceAreaRef => area !== null);
      const manageableAreasSource = Array.isArray(cached.manageableAreas)
        ? cached.manageableAreas
        : cached.areas;
      const manageableAreas = manageableAreasSource
        .map(area => normalizeWorkspaceAreaRef(area, workspaceId))
        .filter((area): area is WorkspaceAreaRef => area !== null);
      return { areas, manageableAreas };
    } catch {
      return null;
    }
  }

  private writeLocalCache(
    areas: WorkspaceAreaRef[],
    manageableAreas: WorkspaceAreaRef[]
  ): void {
    const workspaceId = this.workspaceId;
    const cacheOwnerKey = this.cacheOwnerKey;
    const cacheKey = this.cacheKey;
    if (!workspaceId || !cacheOwnerKey || !cacheKey) {
      return;
    }

    const payload: CachedWorkspaceAreaSections = {
      areas,
      cacheOwnerKey,
      manageableAreas,
      updatedAt: Date.now(),
      version: WORKSPACE_PERMISSION_CACHE_VERSION,
      workspaceId,
    };
    try {
      this.globalState.set(cacheKey, payload);
    } catch {
      // Local cache is best-effort; live server data remains authoritative.
    }
  }

  private clearLocalCache(): void {
    const cacheKey = this.cacheKey;
    if (!cacheKey) {
      return;
    }
    try {
      this.globalState.del(cacheKey);
    } catch {
      // Ignore cleanup failures; in-memory fail-closed state is authoritative.
    }
  }

  private loadFromLocalCache(): void {
    if (this.localCacheLoaded) {
      return;
    }
    if (!this.cacheKey) {
      this.isLocalCacheLoaded$.setValue(true);
      return;
    }
    this.localCacheLoaded = true;
    const cached = this.readLocalCache();
    if (cached && this.areas$.value.length === 0) {
      this.areas$.setValue(cached.areas);
      this.manageableAreas$.setValue(cached.manageableAreas);
      this.isPopulated$.setValue(true);
      this.isStale$.setValue(true);
    }
    this.isLocalCacheLoaded$.setValue(true);
  }

  private ensureFetched(): void {
    if (this.fetchTriggered) {
      return;
    }
    this.fetchTriggered = true;

    this.startReconnectListener();
    this.startBackendAvailabilityListener();
    this.startVisibilityListener();

    this.loadFromLocalCache();
    this.refresh()
      .then(() => {
        if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
          this.scheduleRetry(0);
        }
      })
      .catch(() => {
        this.scheduleRetry(0);
      });
  }

  prime(): void {
    this.ensureFetched();
  }

  private static readonly RETRY_DELAYS_MS = [500, 1000, 2000, 5000];

  private scheduleRetry(attempt: number): void {
    if (this.retryTimer) {
      return;
    }
    if (attempt >= WorkspaceAreaCacheStore.RETRY_DELAYS_MS.length) {
      if (!this.isPopulated$.value) {
        this.isPopulated$.setValue(true);
      }
      return;
    }
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.loadFromLocalCache();
      this.refresh()
        .then(() => {
          if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
            this.scheduleRetry(attempt + 1);
          }
        })
        .catch(() => {
          this.scheduleRetry(attempt + 1);
        });
    }, WorkspaceAreaCacheStore.RETRY_DELAYS_MS[attempt]);
  }

  async refresh(): Promise<void> {
    if (this.refreshPromise) {
      this.refreshAgain = true;
      return this.refreshPromise;
    }

    const promise = (async () => {
      do {
        this.refreshAgain = false;
        await this.fetchFromServer();
      } while (this.refreshAgain);
    })().finally(() => {
      if (this.refreshPromise === promise) {
        this.refreshPromise = null;
      }
    });
    this.refreshPromise = promise;
    return promise;
  }

  private async fetchFromServer(): Promise<void> {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') {
      return;
    }

    const revisionAtStart = this.areaStateRevision;
    const areasAtStart = this.areas$.value;
    const manageableAreasAtStart = this.manageableAreas$.value;
    let areas: WorkspaceAreaRef[];
    let manageableAreas: WorkspaceAreaRef[];
    try {
      ({ areas, manageableAreas } = await this.workspaceAreaApi.bootstrapAreas(
        workspace.id
      ));
      this.authoritativeAccessDenied = false;
    } catch (error) {
      if (revisionAtStart !== this.areaStateRevision) {
        this.refreshAgain = true;
        return;
      }
      if (this.failClosedForAuthoritativeAccessError(error)) {
        return;
      }
      throw error;
    }

    if (revisionAtStart !== this.areaStateRevision) {
      // A create/update/trash response committed newer client state while this
      // snapshot was in flight. Preserve only the exact local diff over the
      // request baseline while still accepting unrelated server changes.
      areas = mergeNewerLocalAreaState(areas, areasAtStart, this.areas$.value);
      manageableAreas = mergeNewerLocalAreaState(
        manageableAreas,
        manageableAreasAtStart,
        this.manageableAreas$.value
      );
    }

    this.areas$.setValue(areas);
    this.manageableAreas$.setValue(manageableAreas);
    this.writeLocalCache(areas, manageableAreas);
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
    this.isServerConfirmed$.setValue(true);
    this.isStale$.setValue(false);
    this.serverFetchCompleted = true;
  }

  private failClosedForAuthoritativeAccessError(error: unknown): boolean {
    if (!isBackendAuthOrPermissionError(error) || isBackendKnownUnavailable()) {
      return false;
    }

    this.areas$.setValue([]);
    this.manageableAreas$.setValue([]);
    this.authoritativeAccessDenied = true;
    this.serverFetchCompleted = false;
    this.isPopulated$.setValue(true);
    this.isLocalCacheLoaded$.setValue(true);
    this.isServerConfirmed$.setValue(false);
    this.isStale$.setValue(false);
    this.clearLocalCache();
    return true;
  }

  private setAreaLists(input: {
    areas?: WorkspaceAreaRef[];
    manageableAreas?: WorkspaceAreaRef[];
  }): void {
    this.areaStateRevision += 1;
    const areas = input.areas ?? this.areas$.value;
    const manageableAreas =
      input.manageableAreas ?? this.manageableAreas$.value;
    this.areas$.setValue(areas);
    this.manageableAreas$.setValue(manageableAreas);
    this.writeLocalCache(areas, manageableAreas);
    this.isPopulated$.setValue(true);
  }

  async createTeamspace(name: string): Promise<WorkspaceAreaRef> {
    const workspace = this.workspaceService.workspace;
    const localMutationId = this.beginLocalMutation('create');
    try {
      const area = await this.workspaceAreaApi.createTeamspace(
        workspace.id,
        name,
        localMutationId
      );
      this.setAreaLists({
        areas: this.mergeArea(this.areas$.value, area),
        manageableAreas: this.mergeArea(this.manageableAreas$.value, area),
      });
      this.completeLocalMutation(localMutationId);
      return area;
    } catch (error) {
      this.forgetLocalMutation(localMutationId);
      throw error;
    }
  }

  async updateTeamspace(
    rootFolderId: string,
    patch: { name?: string }
  ): Promise<WorkspaceAreaRef> {
    const workspace = this.workspaceService.workspace;
    const localMutationId = this.beginLocalMutation('update');
    try {
      const area = await this.workspaceAreaApi.updateTeamspace(
        workspace.id,
        rootFolderId,
        patch,
        localMutationId
      );
      this.setAreaLists({
        areas: this.areas$.value.some(
          current => current.rootFolderId === area.rootFolderId
        )
          ? this.mergeArea(this.areas$.value, area)
          : this.areas$.value,
        manageableAreas: this.mergeArea(this.manageableAreas$.value, area),
      });
      this.completeLocalMutation(localMutationId);
      return area;
    } catch (error) {
      this.forgetLocalMutation(localMutationId);
      throw error;
    }
  }

  async trashTeamspace(rootFolderId: string): Promise<void> {
    const workspace = this.workspaceService.workspace;
    const localMutationId = this.beginLocalMutation('trash');
    try {
      await this.workspaceAreaApi.trashTeamspace(
        workspace.id,
        rootFolderId,
        localMutationId
      );
      this.setAreaLists({
        areas: this.areas$.value.filter(
          area => area.rootFolderId !== rootFolderId
        ),
        manageableAreas: this.manageableAreas$.value.filter(
          area => area.rootFolderId !== rootFolderId
        ),
      });
      this.completeLocalMutation(localMutationId);
    } catch (error) {
      this.forgetLocalMutation(localMutationId);
      throw error;
    }
  }

  private beginLocalMutation(operation: 'create' | 'trash' | 'update'): string {
    const localMutationId = createLocalMutationId(operation);
    this.localMutationEchoes.set(localMutationId, null);
    return localMutationId;
  }

  private completeLocalMutation(localMutationId: string): void {
    if (!this.localMutationEchoes.has(localMutationId)) {
      return;
    }
    const timer = setTimeout(
      () => this.localMutationEchoes.delete(localMutationId),
      LOCAL_MUTATION_ECHO_TTL_MS
    );
    this.localMutationEchoes.set(localMutationId, timer);
  }

  private forgetLocalMutation(localMutationId: string): boolean {
    const timer = this.localMutationEchoes.get(localMutationId);
    if (timer) {
      clearTimeout(timer);
    }
    return this.localMutationEchoes.delete(localMutationId);
  }

  private mergeArea(
    areas: WorkspaceAreaRef[],
    area: WorkspaceAreaRef
  ): WorkspaceAreaRef[] {
    const next = areas.filter(
      current => current.rootFolderId !== area.rootFolderId
    );
    next.push(area);
    return next.sort((left, right) => {
      const byKind = areaSortRank(left.kind) - areaSortRank(right.kind);
      return byKind === 0 ? left.name.localeCompare(right.name) : byKind;
    });
  }

  private startReconnectListener(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.onlineHandler = () => this.scheduleConnectivityRefresh();
    window.addEventListener('online', this.onlineHandler);
  }

  private startBackendAvailabilityListener(): void {
    if (this.backendAvailabilityUnsubscribe) {
      return;
    }

    this.backendAvailabilityUnsubscribe = onBackendAvailable(() => {
      this.scheduleConnectivityRefresh();
    });
  }

  private scheduleConnectivityRefresh(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.refresh()
        .then(() => {
          if (!this.serverFetchCompleted && !this.authoritativeAccessDenied) {
            this.scheduleRetry(0);
          }
        })
        .catch(() => {});
    }, 1000);
  }

  private startVisibilityListener(): void {
    if (typeof document === 'undefined') {
      return;
    }

    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (!this.serverFetchCompleted) {
        return;
      }
      this.refresh().catch(() => {});
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  reconcileMetadataChange(event: WorkspaceMetadataChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;
    if (
      event.changeType === 'area_created' ||
      event.changeType === 'area_updated' ||
      event.changeType === 'area_trashed'
    ) {
      if (
        event.localMutationId &&
        this.forgetLocalMutation(event.localMutationId)
      ) {
        return;
      }
      if (event.docId) {
        void this.reconcileSingleTeamspaceChange(workspace.id, event.docId);
        return;
      }
      void this.refresh().catch(() => {});
    }
  }

  reconcilePermissionChange(event: WorkspacePermissionChange): void {
    const workspace = this.workspaceService.workspace;
    if (!workspace || event.workspaceId !== workspace.id) return;
    if (
      event.changeType === 'area_role_granted' ||
      event.changeType === 'area_role_revoked'
    ) {
      const rootFolderIds = [
        ...new Set([
          ...(event.affectedFolderIds ?? []),
          ...(event.revokedFolderIds ?? []),
        ]),
      ];
      if (rootFolderIds.length !== 1) {
        void this.refresh().catch(() => {});
        return;
      }
      void this.reconcileSingleTeamspaceChange(workspace.id, rootFolderIds[0]);
    }
  }

  private async reconcileSingleTeamspaceChange(
    workspaceId: string,
    rootFolderId: string
  ): Promise<void> {
    try {
      const area = await this.workspaceAreaApi.getAccessibleTeamspace(
        workspaceId,
        rootFolderId
      );
      const manageable =
        area &&
        (area.effectiveRole === 'Owner' || area.effectiveRole === 'Admin');
      this.setAreaLists({
        areas: area
          ? this.mergeArea(this.areas$.value, area)
          : this.areas$.value.filter(
              current => current.rootFolderId !== rootFolderId
            ),
        manageableAreas:
          area && manageable
            ? this.mergeArea(this.manageableAreas$.value, area)
            : this.manageableAreas$.value.filter(
                current => current.rootFolderId !== rootFolderId
              ),
      });
    } catch {
      // A scoped lookup is an optimization. Preserve the full authoritative
      // refresh as the fail-safe for transport and unexpected server errors.
      await this.refresh();
    }
  }

  override dispose(): void {
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.backendAvailabilityUnsubscribe) {
      this.backendAvailabilityUnsubscribe();
      this.backendAvailabilityUnsubscribe = null;
    }
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    for (const timer of this.localMutationEchoes.values()) {
      if (timer) {
        clearTimeout(timer);
      }
    }
    this.localMutationEchoes.clear();
    super.dispose();
  }

  watchAreas(): Observable<WorkspaceAreaRef[]> {
    this.ensureFetched();
    return this.areas$.pipe(map(areas => areas));
  }

  watchManageableAreas(): Observable<WorkspaceAreaRef[]> {
    this.ensureFetched();
    return this.manageableAreas$.pipe(map(areas => areas));
  }

  getCachedAreas(): WorkspaceAreaRef[] {
    return this.areas$.value;
  }

  getCachedManageableAreas(): WorkspaceAreaRef[] {
    return this.manageableAreas$.value;
  }

  hasCompletedServerFetch(): boolean {
    return this.serverFetchCompleted;
  }
}

function areaSortRank(kind: WorkspaceAreaRef['kind']): number {
  switch (kind) {
    case 'private':
      return 0;
    case 'public':
      return 1;
    case 'teamspace':
      return 2;
    case 'app':
      return 3;
  }
}
